import { useEffect, useRef, useState } from "react";
import { add, diff, moveDown, moveInto, moveUp, remove, setProperty, setTheme, setThemeColor, setWidth } from "../../../shared/page-model/edits.ts";
import {
	ALIGNS,
	FITS,
	FONTS,
	FONT_WEIGHTS,
	JUSTIFIES,
	LAYOUTS,
	TEXT_ALIGNS,
	VARIANTS,
	parseScreen,
	type Element,
	type FrameElement,
	type ImageElement,
	type Kind,
	type Screen,
	type TextElement,
} from "../../../shared/page-model/vocabulary.ts";
import type { ComponentProps } from "./index";
import { isGone, parseInteractiveResult, submitInteractiveResult } from "./interactive";
import { Page } from "./Page";
import { addTarget, ancestors, describe, draftKey, elementOf, padding4, paddingFrom4, sourceDraftKey } from "./pageEditorLogic.ts";
import { useWorkspaceJson } from "./useWorkspaceJson";

type SubmitState =
	| { phase: "idle" }
	| { phase: "submitting" }
	| { phase: "submitted"; saved: boolean; changes: number }
	| { phase: "failed"; status: number; message: string };

interface Draft {
	source: string;
	first: Screen;
	model: Screen;
}

/**
 * Renders `{ source, title? }` as the screen model drawn live with a panel to edit it in place. Every
 * edit is a pure function from shared/page-model/edits.ts; 保存 posts the model and diff(first, last),
 * and the components route writes the file before the model hears of it.
 */
export function PageEditor({ sessionId, callId, args, result }: ComponentProps): React.ReactNode {
	const source = typeof args.source === "string" ? args.source : undefined;
	const title = typeof args.title === "string" ? args.title : undefined;
	const { data, error, loading } = useWorkspaceJson<unknown>(sessionId, source);
	const parsed = data === null ? undefined : parseScreen(data);

	if (!source) return <div className="component error">edit_page 组件缺少 source 参数</div>;
	if (loading) return <div className="component page-editor"><p className="hint">加载中…</p></div>;
	if (error) return <div className="component error">{error}</div>;
	if (!parsed) return null;
	if (!parsed.ok) {
		const r = parsed.refusal;
		return (
			<div className="component page-editor">
				<p className="error">
					页面文件不符合词汇表，无法编辑：{r.element ?? "screen"}.{r.property}：{r.reason}
				</p>
				<Decline sessionId={sessionId} callId={callId} result={result} />
			</div>
		);
	}
	return <Editor key={callId} sessionId={sessionId} callId={callId} source={source} title={title} initial={parsed.screen} result={result} />;
}

/** The one button a refused file leaves: tell the agent nothing changed. */
function Decline({ sessionId, callId, result }: Pick<ComponentProps, "sessionId" | "callId" | "result">): React.ReactNode {
	const [done, setDone] = useState(result !== undefined);
	if (done) return <p className="hint">已放弃</p>;
	return (
		<button
			className="ghost"
			onClick={() => void submitInteractiveResult(sessionId, callId, { saved: false }).then((r) => setDone(r.ok))}
		>
			放弃
		</button>
	);
}

function Editor({
	sessionId,
	callId,
	source,
	title,
	initial,
	result,
}: {
	sessionId: string;
	callId: string;
	source: string;
	title: string | undefined;
	initial: Screen;
	result: ComponentProps["result"];
}): React.ReactNode {
	const [first] = useState(initial);
	const [model, setModel] = useState<Screen>(() => readDraft(draftKey(callId), source)?.model ?? initial);
	const [offered] = useState<Draft | undefined>(() => (readDraft(draftKey(callId), source) ? undefined : readDraft(sourceDraftKey(source), source)));
	const [offer, setOffer] = useState(offered !== undefined);
	const [past, setPast] = useState<Screen[]>([]);
	const [future, setFuture] = useState<Screen[]>([]);
	const [selected, setSelected] = useState<string | null>(null);
	const [previous, setPrevious] = useState<string | null>(null);
	const [adding, setAdding] = useState(false);
	const addRef = useRef<HTMLSpanElement>(null);
	// The add menu closes on a click elsewhere or Escape, like any popover.
	useEffect(() => {
		if (!adding) return;
		const onDown = (e: MouseEvent) => {
			if (!addRef.current?.contains(e.target as Node)) setAdding(false);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setAdding(false);
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [adding]);
	const [submit, setSubmit] = useState<SubmitState>({ phase: "idle" });

	const answered = result ? parseInteractiveResult(result) : undefined;
	const gone = isGone(answered, submit.phase === "failed" ? submit.status : undefined);
	const locked = answered !== undefined || submit.phase === "submitting" || submit.phase === "submitted" || gone;

	// Every edit is mirrored to browser storage, so a call that expires does not lose the work.
	useEffect(() => {
		if (locked) return;
		writeDraft(draftKey(callId), { source, first, model });
		writeDraft(sourceDraftKey(source), { source, first, model });
	}, [model, locked, callId, source, first]);

	function apply(next: Screen) {
		if (next === model) return;
		setPast((p) => [...p, model]);
		setFuture([]);
		setModel(next);
	}
	function undo() {
		const prev = past[past.length - 1];
		if (!prev) return;
		setPast((p) => p.slice(0, -1));
		setFuture((f) => [model, ...f]);
		setModel(prev);
	}
	function redo() {
		const next = future[0];
		if (!next) return;
		setFuture((f) => f.slice(1));
		setPast((p) => [...p, model]);
		setModel(next);
	}
	function select(id: string) {
		if (id === selected) return;
		setPrevious(selected);
		setSelected(id);
	}

	async function send(body: unknown, saved: boolean, changes: number) {
		setSubmit({ phase: "submitting" });
		const outcome = await submitInteractiveResult(sessionId, callId, body);
		if (outcome.ok) {
			setSubmit({ phase: "submitted", saved, changes });
			clearDraft(draftKey(callId));
			if (saved) clearDraft(sourceDraftKey(source));
			return;
		}
		setSubmit({ phase: "failed", status: outcome.status, message: outcome.message });
	}
	function save() {
		const changes = diff(first, model);
		void send({ saved: true, model, changes }, true, changes.length);
	}
	function decline() {
		void send({ saved: false }, false, 0);
	}

	const element = selected ? elementOf(model, selected) : undefined;
	const previousFrame = previous ? elementOf(model, previous) : undefined;
	const canMoveInto = Boolean(element && previousFrame?.kind === "frame" && element.id !== model.root.id && previousFrame.id !== element.id);
	const chain = selected ? ancestors(model, selected) : [];

	return (
		<div className="component page-editor">
			{title && <p className="component-prompt">{title}</p>}
			{offer && offered && !locked && (
				<p className="hint page-editor-offer">
					恢复上次未保存的修改？
					<button className="ghost" onClick={() => { apply(offered.model); setOffer(false); }}>恢复</button>
					<button className="ghost" onClick={() => setOffer(false)}>忽略</button>
				</p>
			)}
			<Page screen={model} selected={selected} onSelect={locked ? () => {} : select} sessionId={sessionId} source={source} />
			{!locked && (
				<>
					<div className="page-crumbs">
						{chain.map((e, i) => (
							<span key={e.id}>
								{i > 0 && <span className="hint"> › </span>}
								<button className="ghost crumb" onClick={() => select(e.id)}>{describe(e)}</button>
							</span>
						))}
					</div>
					<div className="page-panel">
						{element ? (
							<ElementPanel model={model} element={element} onChange={apply} />
						) : (
							<ThemePanel model={model} onChange={apply} />
						)}
					</div>
					<div className="page-toolbar">
						<button className="ghost" onClick={undo} disabled={past.length === 0}>撤销</button>
						<button className="ghost" onClick={redo} disabled={future.length === 0}>重做</button>
						<button className="ghost" onClick={() => selected && apply(moveUp(model, selected))} disabled={!element || element.id === model.root.id}>上移</button>
						<button className="ghost" onClick={() => selected && apply(moveDown(model, selected))} disabled={!element || element.id === model.root.id}>下移</button>
						<button className="ghost" onClick={() => { if (selected) { apply(remove(model, selected)); setSelected(null); } }} disabled={!element || element.id === model.root.id}>删除</button>
						<span className="page-add" ref={addRef}>
							<button className={adding ? "ghost open" : "ghost"} onClick={() => setAdding((a) => !a)}>添加</button>
							{adding && (
								<span className="page-add-menu">
									{(["text", "button", "image", "frame"] as Kind[]).map((kind) => (
										<button key={kind} className="ghost" onClick={() => {
											const target = addTarget(model, selected);
											const next = add(model, target.parent, target.index, kind);
											apply(next);
											setAdding(false);
											select(`e${model.nextId}`);
										}}>
											{KIND_LABEL[kind]}
										</button>
									))}
								</span>
							)}
						</span>
						<button className="ghost" onClick={() => selected && previous && apply(moveInto(model, selected, previous))} disabled={!canMoveInto} title={previousFrame?.kind === "frame" ? `移入 ${describe(previousFrame)}` : undefined}>移入</button>
						<span className="page-toolbar-gap" />
						<button onClick={save}>保存</button>
						<button className="ghost" onClick={decline}>放弃</button>
					</div>
					{submit.phase === "failed" && <p className="error">{submit.message}</p>}
				</>
			)}
			{locked && <Outcome answered={answered} submit={submit} gone={gone} />}
		</div>
	);
}

const KIND_LABEL: Record<Kind, string> = { text: "文字", button: "按钮", image: "图片", frame: "容器" };

/** What a read-only editor says under the page: saved, declined, or expired with the draft kept. */
function Outcome({ answered, submit, gone }: { answered: ReturnType<typeof parseInteractiveResult> | undefined; submit: SubmitState; gone: boolean }): React.ReactNode {
	if (submit.phase === "submitted") return <p className="hint">{submit.saved ? `已保存 · ${submit.changes} 处修改` : "已放弃"}</p>;
	if (answered?.kind === "value") {
		const saved = answered.value.saved === true;
		const changes = Array.isArray(answered.value.changes) ? answered.value.changes.length : 0;
		return <p className="hint">{saved ? `已保存 · ${changes} 处修改` : "已放弃"}</p>;
	}
	if (gone) return <p className="hint">已过期，修改已保留在此设备</p>;
	if (submit.phase === "submitting") return <p className="hint">提交中…</p>;
	return <p className="error">已提交，无法读取结果</p>;
}

function ElementPanel({ model, element, onChange }: { model: Screen; element: Element; onChange: (next: Screen) => void }): React.ReactNode {
	const set = (property: string, value: unknown) => onChange(setProperty(model, element.id, property, value));
	switch (element.kind) {
		case "text":
			return <TextPanel model={model} element={element} set={set} />;
		case "frame":
			return <FramePanel model={model} element={element} set={set} />;
		case "image":
			return <ImagePanel element={element} set={set} />;
		case "button":
			return (
				<div className="form-fields">
					<Row label="文字"><input type="text" value={element.label} onChange={(e) => set("label", e.target.value)} /></Row>
					<Row label="样式"><Choice value={element.variant} options={VARIANTS} onChange={(v) => set("variant", v)} /></Row>
					<SizeRow label="宽度" value={element.width} onChange={(v) => set("width", v)} />
				</div>
			);
	}
}

function TextPanel({ model, element, set }: { model: Screen; element: TextElement; set: (p: string, v: unknown) => void }): React.ReactNode {
	const weights = FONT_WEIGHTS[model.theme.font];
	return (
		<div className="form-fields">
			<Row label="文字"><textarea rows={3} value={element.text} onChange={(e) => set("text", e.target.value)} /></Row>
			<Row label="字号"><Num value={element.size} onChange={(v) => set("size", v)} /></Row>
			<Row label="字重"><Choice value={String(element.weight)} options={weights.map(String)} onChange={(v) => set("weight", Number(v))} /></Row>
			<ColorRow label="颜色" theme={model.theme} value={element.color} onChange={(v) => set("color", v)} />
			<Row label="行高（留空为自动）"><Num value={element.lineHeight} optional onChange={(v) => set("lineHeight", v)} /></Row>
			<Row label="对齐"><Choice value={element.align ?? "left"} options={TEXT_ALIGNS} onChange={(v) => set("align", v === "left" ? null : v)} /></Row>
			<SizeRow label="宽度" value={element.width} onChange={(v) => set("width", v)} />
		</div>
	);
}

function FramePanel({ model, element, set }: { model: Screen; element: FrameElement; set: (p: string, v: unknown) => void }): React.ReactNode {
	const sides = padding4(element.padding ?? 0);
	const [uniform, setUniform] = useState(sides.every((s) => s === sides[0]));
	const isRoot = element.id === model.root.id;
	const setSide = (i: number, v: number) => {
		const next: [number, number, number, number] = uniform ? [v, v, v, v] : [...sides];
		if (!uniform) next[i] = v;
		set("padding", paddingFrom4(next));
	};
	return (
		<div className="form-fields">
			<Row label="排列"><Choice value={element.layout} options={LAYOUTS} labels={{ column: "纵向", row: "横向" }} onChange={(v) => set("layout", v)} /></Row>
			<Row label="间距"><Num value={element.gap ?? 0} onChange={(v) => set("gap", v || null)} /></Row>
			<div className="form-field">
				<span className="form-field-label">
					内边距
					<label className="page-inline"><input type="checkbox" checked={uniform} onChange={(e) => { setUniform(e.target.checked); if (e.target.checked) set("padding", sides[0] || null); }} /> 四边相同</label>
				</span>
				{uniform ? (
					<Num value={sides[0]} onChange={(v) => setSide(0, v ?? 0)} />
				) : (
					<div className="page-sides">
						{(["上", "右", "下", "左"] as const).map((name, i) => (
							<label key={name} className="page-side">{name}<Num value={sides[i]} onChange={(v) => setSide(i, v ?? 0)} /></label>
						))}
					</div>
				)}
			</div>
			<Row label="交叉轴对齐"><Choice value={element.align ?? "start"} options={ALIGNS} labels={{ start: "起点", center: "居中", end: "终点" }} onChange={(v) => set("align", v === "start" ? null : v)} /></Row>
			<Row label="主轴分布"><Choice value={element.justify ?? "start"} options={JUSTIFIES} labels={{ start: "起点", center: "居中", end: "终点", between: "两端" }} onChange={(v) => set("justify", v === "start" ? null : v)} /></Row>
			{!isRoot && <SizeRow label="宽度" value={element.width} onChange={(v) => set("width", v)} />}
			<SizeRow label="高度" value={element.height} onChange={(v) => set("height", v)} />
			<ColorRow label="填充" theme={model.theme} value={element.fill} optional onChange={(v) => set("fill", v)} />
			<ColorRow label="描边" theme={model.theme} value={element.stroke} optional onChange={(v) => set("stroke", v)} />
			{element.stroke && <Row label="描边宽度"><Num value={element.strokeWidth ?? 1} onChange={(v) => set("strokeWidth", v)} /></Row>}
			<Row label="圆角"><Num value={element.radius ?? 0} onChange={(v) => set("radius", v || null)} /></Row>
		</div>
	);
}

function ImagePanel({ element, set }: { element: ImageElement; set: (p: string, v: unknown) => void }): React.ReactNode {
	return (
		<div className="form-fields">
			<Row label="图片（placeholder，或 assets/ 下的路径）"><input type="text" value={element.src} onChange={(e) => set("src", e.target.value || "placeholder")} /></Row>
			<Row label="宽度"><Num value={element.width} onChange={(v) => set("width", v ?? element.width)} /></Row>
			<Row label="高度"><Num value={element.height} onChange={(v) => set("height", v ?? element.height)} /></Row>
			<Row label="圆角"><Num value={element.radius ?? 0} onChange={(v) => set("radius", v || null)} /></Row>
			<Row label="裁切"><Choice value={element.fit ?? "cover"} options={FITS} onChange={(v) => set("fit", v === "cover" ? null : v)} /></Row>
		</div>
	);
}

function ThemePanel({ model, onChange }: { model: Screen; onChange: (next: Screen) => void }): React.ReactNode {
	return (
		<div className="form-fields">
			<p className="component-prompt">主题</p>
			<Row label="字体"><Choice value={model.theme.font} options={FONTS} onChange={(v) => onChange(setTheme(model, "font", v as Screen["theme"]["font"]))} /></Row>
			{Object.entries(model.theme.colors).map(([name, hex]) => (
				<Row key={name} label={`颜色 ${name}`}><Hex value={hex} onChange={(v) => onChange(setThemeColor(model, name, v))} /></Row>
			))}
			<Row label="页面宽度"><Num value={model.width} onChange={(v) => v && onChange(setWidth(model, v))} /></Row>
		</div>
	);
}

function Row({ label, children }: { label: string; children: React.ReactNode }): React.ReactNode {
	return (
		<label className="form-field">
			<span className="form-field-label">{label}</span>
			{children}
		</label>
	);
}

function Choice<T extends string>({ value, options, labels, onChange }: { value: T; options: readonly T[]; labels?: Partial<Record<T, string>>; onChange: (v: T) => void }): React.ReactNode {
	return (
		<select value={value} onChange={(e) => onChange(e.target.value as T)}>
			{options.map((o) => (
				<option key={o} value={o}>{labels?.[o] ?? o}</option>
			))}
		</select>
	);
}

/** Whole pixels; an empty optional field means "none". */
function Num({ value, optional, onChange }: { value: number | undefined; optional?: boolean; onChange: (v: number | null) => void }): React.ReactNode {
	return (
		<input
			type="number"
			min={0}
			step={1}
			value={value ?? ""}
			onChange={(e) => {
				if (e.target.value === "") return onChange(optional ? null : 0);
				onChange(Math.max(0, Math.round(Number(e.target.value))));
			}}
		/>
	);
}

const SIZE_MODES = ["hug", "fill", "px"] as const;
const SIZE_LABEL = { hug: "适应内容", fill: "填充容器", px: "像素" } as const;

/** hug, fill or pixels, with the number shown only for pixels. */
function SizeRow({ label, value, onChange }: { label: string; value: "hug" | "fill" | number | undefined; onChange: (v: "hug" | "fill" | number | null) => void }): React.ReactNode {
	const mode = typeof value === "number" ? "px" : (value ?? "hug");
	return (
		<div className="form-field">
			<span className="form-field-label">{label}</span>
			<div className="page-size">
				<Choice value={mode} options={SIZE_MODES} labels={SIZE_LABEL} onChange={(m) => onChange(m === "hug" ? null : m === "fill" ? "fill" : 200)} />
				{mode === "px" && <Num value={typeof value === "number" ? value : 200} onChange={(v) => onChange(v || 1)} />}
			</div>
		</div>
	);
}

/** Theme names first, then a hex; optional colours can be cleared. */
function ColorRow({ label, theme, value, optional, onChange }: { label: string; theme: Screen["theme"]; value: string | undefined; optional?: boolean; onChange: (v: string | null) => void }): React.ReactNode {
	const names = Object.keys(theme.colors);
	const isName = value !== undefined && names.includes(value);
	const mode = value === undefined ? "none" : isName ? value : "hex";
	return (
		<div className="form-field">
			<span className="form-field-label">{label}</span>
			<div className="page-size">
				<select value={mode} onChange={(e) => onChange(e.target.value === "none" ? null : e.target.value === "hex" ? (isName ? theme.colors[value] ?? "#000000" : value ?? "#000000") : e.target.value)}>
					{optional && <option value="none">无</option>}
					{names.map((n) => (
						<option key={n} value={n}>{n} {theme.colors[n]}</option>
					))}
					<option value="hex">自定义</option>
				</select>
				{mode === "hex" && <Hex value={value ?? "#000000"} onChange={onChange} />}
			</div>
		</div>
	);
}

function Hex({ value, onChange }: { value: string; onChange: (v: string) => void }): React.ReactNode {
	return (
		<span className="page-hex">
			<input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} />
			<input type="text" value={value} onChange={(e) => /^#[0-9a-fA-F]{6}$/.test(e.target.value) && onChange(e.target.value.toUpperCase())} />
		</span>
	);
}

function readDraft(key: string, source: string): Draft | undefined {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return undefined;
		const draft = JSON.parse(raw) as Draft;
		if (draft.source !== source || !parseScreen(draft.model).ok) return undefined;
		return draft;
	} catch {
		return undefined;
	}
}

function writeDraft(key: string, draft: Draft) {
	try {
		localStorage.setItem(key, JSON.stringify(draft));
	} catch {
		// Storage can be full or blocked; the draft is a convenience, never the truth.
	}
}

function clearDraft(key: string) {
	try {
		localStorage.removeItem(key);
	} catch {
		// Same as above.
	}
}
